import React, { useState, useEffect, useMemo, useCallback } from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import { useTheme } from '../contexts/ThemeContext';
import { useAuth } from '../contexts/AuthContext';
import { useUiLayoutPreferences } from '../contexts/UiLayoutPreferencesContext';
import ProfileModal from './modals/ProfileModal';
import { getMenuItemsByRole } from '../config/menuConfig';
import { TICKETING_DATA_CHANGED_EVENT } from '../modules/ticketing/ticketingStorage';
import { fetchTicketingDepartments } from '../modules/ticketing/ticketingApi';
import { getUIConfig } from '../config/appConfig';
import { cn } from '../lib/utils';
import { useHiddenMenuHrefs } from '../hooks/useApplicationSetting';
import {
  mergeTicketingDepartmentsIntoMenu,
  buildVisibleSidebarMenu,
  filterMenuBySearch,
  getExpandPathsForPathname,
  collectMenuLinks,
  collectMenuHrefs,
  pathnameMatchesHref,
  resolveFavoriteItems,
  loadSidebarFavorites,
  saveSidebarFavorites,
  loadSidebarExpanded,
  saveSidebarExpanded,
  toggleSidebarExpandedAccordion,
  annotateSectionsForFlatList,
  SECTION_COLLAPSED_STRIPE,
  MAX_SIDEBAR_FAVORITES,
  collapsedMenuTitle,
} from '../config/menuSidebarUtils';
import SidebarSectionHeader from './sidebar/SidebarSectionHeader';
import SidebarMenuSearch from './sidebar/SidebarMenuSearch';
import { API_BASE_URL } from '../config/apiConfig';
import { resolveCompanyLogoUrl } from '../utils/companyLogo';
import useSWR from 'swr';
import { 
  ArrowLeftIcon, 
  XIcon, 
  ShieldCheckIcon, 
  FolderPlusIcon, 
  ArrowRightIcon,
  DocumentTextIcon,
  UserIcon,
  ArrowRightOnRectangleIcon,
  ChevronDownIcon,
  ChevronLeftIcon,
  StarIcon,
} from './ui/Icons';

const Sidebar = ({ isOpen, setIsOpen, userRole = 'admin', onLogout, collapsed = false, setCollapsed }) => {
  const [expandedItems, setExpandedItems] = useState(() => loadSidebarExpanded());
  const [menuSearch, setMenuSearch] = useState('');
  const [favoriteHrefs, setFavoriteHrefs] = useState(() => loadSidebarFavorites());
  const [isProfileModalOpen, setIsProfileModalOpen] = useState(false);
  const [activeDrawer, setActiveDrawer] = useState(null); // null, 'level2', 'level3', 'level4'
  const [drawerData, setDrawerData] = useState({
    level2: null,
    level3: null,
    level4: null
  });
  const [drawerHistory, setDrawerHistory] = useState([]);
  const [ticketingMenuTick, setTicketingMenuTick] = useState(0);
  const [ticketingDepts, setTicketingDepts] = useState([]);
  const [companyLogoFailed, setCompanyLogoFailed] = useState(false);
  const navigate = useNavigate();
  const location = useLocation();
  const { theme, isDark } = useTheme();
  const { user } = useAuth();
  const { showSidebarGroups, showSidebarSearch, showSidebarFavorites } =
    useUiLayoutPreferences();
  const { hiddenHrefs } = useHiddenMenuHrefs();

  // Fetch app version from ApplicationSettings
  const fetcher = async (url) => {
    const token = localStorage.getItem('authToken');
    const response = await fetch(url, {
      headers: {
        'Content-Type': 'application/json',
        ...(token ? { Authorization: `Bearer ${token}` } : {})
      }
    });
    if (!response.ok) throw new Error('Failed to fetch');
    return response.json();
  };
  
  const { data: appVersionSetting } = useSWR(
    `${API_BASE_URL}/application-setting/key/appVersion`,
    fetcher
  );

  const { data: companyLogoSetting } = useSWR(
    `${API_BASE_URL}/application-setting/key/companyLogo`,
    fetcher,
  );

  const appVersion = appVersionSetting?.value || null;

  const companyLogoUrl = useMemo(() => {
    if (companyLogoFailed) return null;
    if (!companyLogoSetting?.value || companyLogoSetting.isActive === false) {
      return null;
    }
    return resolveCompanyLogoUrl(companyLogoSetting.value);
  }, [companyLogoSetting, companyLogoFailed]);

  useEffect(() => {
    setCompanyLogoFailed(false);
  }, [companyLogoSetting?.value]);

  const useBrandHeader = Boolean(companyLogoUrl);

  /** Same surface + border as top `Header.js` */
  const topHeaderMatchClass = cn(
    theme.colors.surface,
    theme.shadow.sm,
    theme.border.light,
    'border-b',
  );

  const logoCollapseBtnClass = cn(
    'border shadow-sm transition-all duration-200',
    theme.border.light,
    isDark
      ? 'bg-slate-800 hover:bg-slate-700 text-slate-300'
      : 'bg-white hover:bg-slate-50 text-slate-600',
  );

  // Get sidebar configuration
  const sidebarConfig = getUIConfig('sidebar') || {};
  const isCollapsible = sidebarConfig.collapsible !== false;
  const collapsedWidth = sidebarConfig.collapsedWidth || '64px';
  const expandedWidth = sidebarConfig.expandedWidth || '288px';

  useEffect(() => {
    const refreshTicketingMenu = () => setTicketingMenuTick((t) => t + 1);
    window.addEventListener(TICKETING_DATA_CHANGED_EVENT, refreshTicketingMenu);
    return () => {
      window.removeEventListener(TICKETING_DATA_CHANGED_EVENT, refreshTicketingMenu);
    };
  }, []);

  useEffect(() => {
    let cancelled = false;
    (async () => {
      try {
        const depts = await fetchTicketingDepartments();
        if (!cancelled) setTicketingDepts(Array.isArray(depts) ? depts : []);
      } catch {
        if (!cancelled) setTicketingDepts([]);
      }
    })();
    return () => {
      cancelled = true;
    };
  }, [ticketingMenuTick]);

  const effectivePathname = useMemo(() => {
    const params = new URLSearchParams(location.search || '');
    if (location.pathname === '/todo' && params.get('from') === 'calendar') {
      return '/todo/calendar';
    }
    return location.pathname;
  }, [location.pathname, location.search]);

  useEffect(() => {
    const base = getMenuItemsByRole(userRole);
    const merged = mergeTicketingDepartmentsIntoMenu(base, ticketingDepts);
    const visible = buildVisibleSidebarMenu(merged, user, hiddenHrefs);
    const paths = getExpandPathsForPathname(visible, effectivePathname);
    setExpandedItems(paths);
    saveSidebarExpanded(paths);
  }, [effectivePathname, userRole, ticketingDepts, user, hiddenHrefs]);

  // Cleanup on unmount
  useEffect(() => {
    return () => {
      // Cleanup any pending timeouts or state
      setActiveDrawer(null);
      setDrawerData({
        level2: null,
        level3: null,
        level4: null
      });
      setDrawerHistory([]);
    };
  }, []);

  const toggleCollapsed = () => {
    if (setCollapsed) {
      setCollapsed(!collapsed);
    }
  };

  const openDrawer = (level, data) => {
    if (!collapsed) return;
    
    // Store current drawer in history if we're navigating deeper
    if (activeDrawer && level !== activeDrawer) {
      setDrawerHistory(prev => [...prev, { level: activeDrawer, data: drawerData[activeDrawer] }]);
    }
    
    // Clear all drawer data and set only the current level
    setDrawerData({
      level2: null,
      level3: null,
      level4: null,
      [level]: data
    });
    setActiveDrawer(level);
  };

  const closeDrawer = (level) => {
    // Close all drawers and return to collapsed sidebar
    setActiveDrawer(null);
    setDrawerData({
      level2: null,
      level3: null,
      level4: null
    });
    setDrawerHistory([]);
  };

  const goBack = () => {
    if (drawerHistory.length > 0) {
      const previousDrawer = drawerHistory[drawerHistory.length - 1];
      setDrawerHistory(prev => prev.slice(0, -1));
      setDrawerData({
        level2: null,
        level3: null,
        level4: null,
        [previousDrawer.level]: previousDrawer.data
      });
      setActiveDrawer(previousDrawer.level);
    }
  };

  const handleItemClick = (item, path = '') => {
    const fullPath = path ? `${path}.${item.name}` : item.name;
    
    if (item.subitems && collapsed) {
      // Open drawer for subitems
      openDrawer('level2', { item, fullPath });
    } else if (item.href) {
      // Navigate to the page
      navigate(item.href);
      // Close all drawers
      setActiveDrawer(null);
      setDrawerData({
        level2: null,
        level3: null,
        level4: null
      });
      // Close mobile sidebar after navigation
      if (isOpen) {
        setIsOpen(false);
      }
    }
  };

  const toggleExpanded = (itemPath) => {
    setExpandedItems((prev) => {
      const next = toggleSidebarExpandedAccordion(prev, itemPath);
      saveSidebarExpanded(next);
      return next;
    });
  };

  const toggleFavorite = (href) => {
    if (!href) return;
    setFavoriteHrefs((prev) => {
      let next;
      if (prev.includes(href)) {
        next = prev.filter((h) => h !== href);
      } else if (prev.length >= MAX_SIDEBAR_FAVORITES) {
        next = [...prev.slice(1), href];
      } else {
        next = [...prev, href];
      }
      saveSidebarFavorites(next);
      return next;
    });
  };

  const handleExpandedItemClick = (item, path = '') => {
    const fullPath = path ? `${path}.${item.name}` : item.name;
    
    if (item.subitems) {
      // If item has subitems, toggle expansion
      toggleExpanded(fullPath);
    } else if (item.href) {
      // If item has href, navigate to it using React Router
      navigate(item.href);
      // Close mobile sidebar after navigation
      if (isOpen) {
        setIsOpen(false);
      }
    }
  };

  const visibleMenuItems = useMemo(() => {
    const base = getMenuItemsByRole(userRole);
    const merged = mergeTicketingDepartmentsIntoMenu(base, ticketingDepts);
    return buildVisibleSidebarMenu(merged, user, hiddenHrefs);
  }, [userRole, ticketingDepts, user, hiddenHrefs]);

  const allMenuLinks = useMemo(
    () => collectMenuLinks(visibleMenuItems),
    [visibleMenuItems]
  );

  const menuHrefs = useMemo(() => allMenuLinks.map((link) => link.href), [allMenuLinks]);

  const isCurrentPath = useCallback(
    (href) => pathnameMatchesHref(href, effectivePathname, menuHrefs),
    [effectivePathname, menuHrefs],
  );

  const hasActiveChild = useCallback(
    (item) => {
      if (item.href && isCurrentPath(item.href)) return true;
      if (item.subitems) {
        return item.subitems.some((subitem) => hasActiveChild(subitem));
      }
      return false;
    },
    [isCurrentPath],
  );

  const favoriteLinks = useMemo(
    () => resolveFavoriteItems(allMenuLinks, favoriteHrefs),
    [allMenuLinks, favoriteHrefs]
  );

  const displayMenuItems = useMemo(() => {
    const annotated = annotateSectionsForFlatList(visibleMenuItems);
    const searched = filterMenuBySearch(annotated, menuSearch);
    if (showSidebarGroups) return searched;
    return searched.filter((item) => !item.isHeader);
  }, [visibleMenuItems, menuSearch, showSidebarGroups]);

  // Recursive component to render menu items at any level
  const sidebarActiveItemClass = isActive =>
    isActive
      ? theme.sidebar?.selected ||
        `${theme.colors.primary} ${theme.text.inverse} ${theme.shadow.lg} scale-[1.02]`
      : '';

  const sidebarActiveIconClass = isActive =>
    isActive ? theme.sidebar?.activeIcon || theme.text.inverse : null;

  const sidebarActiveDotClass = isActive =>
    isActive
      ? theme.sidebar?.activeDot || theme.text.inverse.replace('text-', 'bg-')
      : null;

  const renderMenuItem = (item, level = 0, path = '') => {
    const fullPath = path ? `${path}.${item.name}` : item.name;
    const isExpanded = expandedItems[fullPath];
    const isActive = item.href && isCurrentPath(item.href);
    const hasActiveChildItem = hasActiveChild(item);
    const indentLevel = level * 12; // 12px per level

    if (item.isHeader) {
      if (!showSidebarGroups) return null;
      return (
        <SidebarSectionHeader
          key={fullPath}
          item={item}
          collapsed={collapsed}
        />
      );
    }

    const sectionStripe =
      SECTION_COLLAPSED_STRIPE[item._sectionVariant] || SECTION_COLLAPSED_STRIPE.default;
    const isPinned = item.href && favoriteHrefs.includes(item.href);

    return (
      <div key={fullPath}>
        {/* Menu item */}
        <div
          className={`
            relative flex items-center ${collapsed ? 'justify-center px-2' : 'px-3'} py-2.5 text-sm font-medium rounded-xl transition-all duration-300 cursor-pointer group
            ${isActive 
              ? sidebarActiveItemClass(true)
              : hasActiveChildItem
                ? `${theme.colors.background} ${theme.text.primary} ${theme.shadow.sm} border ${theme.border.light}`
                : `${theme.text.secondary} hover:${theme.sidebar?.hover || theme.colors.background} hover:${theme.text.primary} hover:${theme.shadow.default} hover:scale-[1.01]`
            }
          `}
          style={{ marginLeft: collapsed ? '0px' : `${indentLevel}px` }}
          onClick={() => collapsed ? handleItemClick(item, path) : handleExpandedItemClick(item, path)}
          title={collapsed ? collapsedMenuTitle(item) : undefined}
        >
          {collapsed && level === 0 && (
            <div
              className={cn('absolute left-0 top-2 bottom-2 w-1 rounded-r', sectionStripe)}
              aria-hidden
            />
          )}
          {/* Level indicator line for nested items - Only show when not collapsed */}
          {!collapsed && level > 0 && (
            <div className={`absolute left-0 top-0 bottom-0 w-0.5 ${theme.colors.accent}`}></div>
          )}
          
          {/* Icon */}
          {item.icon && (
            <svg
              className={`${collapsed ? 'mr-0' : 'mr-3'} h-5 w-5 transition-colors duration-200 ${
                isActive 
                  ? sidebarActiveIconClass(true) || theme.text.inverse
                  : hasActiveChildItem
                    ? theme.text.primary
                    : `${theme.text.muted} group-hover:${theme.text.primary}`
              }`}
              fill="none"
              viewBox="0 0 24 24"
              stroke="currentColor"
            >
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d={item.icon} />
            </svg>
          )}
          
          {/* Nested level dot indicator */}
          {level > 0 && !item.icon && (
            <div className={`${collapsed ? 'mr-0' : 'mr-3'} h-2 w-2 rounded-full transition-colors duration-200 ${
              isActive 
                ? sidebarActiveDotClass(true)
                : hasActiveChildItem
                  ? theme.colors.accent.replace('bg-', 'bg-')
                  : `${theme.text.muted.replace('text-', 'bg-')} group-hover:${theme.colors.accent.replace('bg-', 'bg-')}`
            }`}></div>
          )}
          
          {/* Fallback for items without icons in collapsed mode */}
          {collapsed && !item.icon && level === 0 && (
            <div className={`h-5 w-5 rounded-full transition-colors duration-200 ${
              isActive 
                ? sidebarActiveDotClass(true)
                : hasActiveChildItem
                  ? theme.colors.accent.replace('bg-', 'bg-')
                  : `${theme.text.muted.replace('text-', 'bg-')} group-hover:${theme.colors.accent.replace('bg-', 'bg-')}`
            }`}></div>
          )}
          
          {!collapsed && <span className="flex-1 truncate">{item.name}</span>}

          {!collapsed && showSidebarFavorites && item.href && (
            <button
              type="button"
              onClick={(e) => {
                e.stopPropagation();
                toggleFavorite(item.href);
              }}
              className={cn(
                'ml-1 p-1 rounded-md opacity-0 group-hover:opacity-100 transition-opacity',
                isPinned ? 'opacity-100' : '',
                theme.text.muted,
                'hover:text-amber-500'
              )}
              title={
                isPinned
                  ? 'Remove from favorites'
                  : `Pin to favorites (max ${MAX_SIDEBAR_FAVORITES})`
              }
            >
              <StarIcon
                className={cn('h-4 w-4', isPinned && 'fill-amber-400 stroke-amber-500')}
              />
            </button>
          )}
          
          {/* Badge for notifications - Only show when not collapsed */}
          {!collapsed && item.badge && (
            <span className={`inline-flex items-center justify-center px-2 py-1 text-xs font-medium ${theme.text.inverse} ${theme.colors.danger} rounded-full ml-2 animate-pulse`}>
              {item.badge}
            </span>
          )}
          
          {/* Expand/Collapse icon for items with subitems - Only show when not collapsed */}
          {!collapsed && item.subitems && (
            <ChevronDownIcon
              className={`h-4 w-4 ml-2 transition-all duration-300 ${
                isExpanded ? 'rotate-180' : ''
              } ${
                isActive 
                  ? sidebarActiveIconClass(true) || theme.text.inverse
                  : hasActiveChildItem
                    ? theme.text.primary
                    : `${theme.text.muted} group-hover:${theme.text.primary}`
              }`}
            />
          )}
          
          {/* Arrow icon for clickable items without subitems - Only show when not collapsed */}
          {!collapsed && item.href && !item.subitems && (
            <ArrowRightIcon
              className={`h-4 w-4 ml-2 transition-all duration-200 opacity-0 group-hover:opacity-100 ${
                isActive
                  ? `${sidebarActiveIconClass(true) || theme.text.inverse} opacity-100`
                  : `${theme.text.muted} group-hover:${theme.text.primary}`
              }`}
            />
          )}
        </div>

        {/* Subitems with smooth animation - Only show when not collapsed */}
        {!collapsed && item.subitems && (
          <div className={`
            overflow-hidden transition-all duration-300 ease-in-out
            ${isExpanded ? 'max-h-[2000px] opacity-100' : 'max-h-0 opacity-0'}
          `}>
            <div className="py-1 space-y-1">
              {item.subitems.map((subitem, subIndex) => 
                renderMenuItem(subitem, level + 1, fullPath)
              )}
            </div>
          </div>
        )}
      </div>
    );
  };

  // Render drawer for collapsed mode
  const renderDrawer = (level, data, leftPosition) => {
    if (!collapsed || !data || activeDrawer !== level) return null;

    const { item } = data;
    if (!item.subitems || item.subitems.length === 0) return null;

    return (
      <div
        className={`fixed top-0 bottom-0 z-40 ${theme.colors.surface} ${theme.shadow.xl} border-r ${theme.border.light} transition-all duration-300 ease-in-out backdrop-blur-sm`}
        style={{
          left: `${leftPosition}px`,
          width: '320px'
        }}
      >
        {/* Drawer Header */}
        <div className={`flex items-center justify-between h-16 px-6 border-b ${theme.border.light} ${theme.gradients.subtle}`}>
          <div className="flex items-center space-x-3">
            {/* Back Button for levels 3 and 4 */}
            {(level === 'level3' || level === 'level4') && drawerHistory.length > 0 && (
              <button
                onClick={goBack}
                className={`p-2 rounded-lg ${theme.text.secondary} hover:${theme.colors.background} hover:${theme.text.primary} transition-all duration-200 ${theme.shadow.sm} hover:${theme.shadow.default}`}
                title="Go back"
              >
                <ArrowLeftIcon className="w-5 h-5" />
              </button>
            )}
            
            <div className={`p-2 ${theme.colors.primary} ${theme.text.inverse} rounded-lg ${theme.shadow.sm}`}>
              <ShieldCheckIcon className={`w-5 h-5 ${theme.text.inverse}`} />
            </div>
            <div>
              <h3 className={`text-lg font-semibold ${theme.text.primary}`}>{item.name}</h3>
              <p className={`text-xs ${theme.text.muted}`}>
                {level === 'level2' ? 'Navigation Menu' : 
                 level === 'level3' ? 'Submenu' : 
                 'Sub-submenu'}
              </p>
            </div>
          </div>
          <button
            onClick={() => closeDrawer(level)}
            className={`p-2 rounded-lg ${theme.text.secondary} hover:${theme.colors.background} hover:${theme.text.primary} transition-all duration-200 ${theme.shadow.sm} hover:${theme.shadow.default}`}
          >
            <XIcon className="w-5 h-5" />
          </button>
        </div>

        {/* Drawer Content */}
        <div className={`flex-1 overflow-y-auto ${theme.gradients.background} py-6`}>
          <div className="space-y-2 px-4">
            {item.subitems.map((subitem, index) => (
              <div key={index}>
                {subitem.subitems && subitem.subitems.length > 0 ? (
                  // Subitem with its own subitems
                  <button
                    onClick={() => {
                      if (level === 'level2') {
                        openDrawer('level3', { item: subitem, fullPath: `${data.fullPath}.${subitem.name}` });
                      } else if (level === 'level3') {
                        openDrawer('level4', { item: subitem, fullPath: `${data.fullPath}.${subitem.name}` });
                      }
                    }}
                    className={`w-full flex items-center justify-between px-4 py-3 text-left ${theme.text.secondary} hover:${theme.colors.background} hover:${theme.text.primary} rounded-xl transition-all duration-200 ${theme.shadow.sm} hover:${theme.shadow.default} hover:scale-[1.02] group`}
                  >
                    <div className="flex items-center space-x-3">
                      <div className={`p-1.5 ${theme.colors.accent} ${theme.text.inverse} rounded-lg ${theme.shadow.sm} group-hover:${theme.shadow.default} transition-all duration-200`}>
                        <FolderPlusIcon className={`w-4 h-4 ${theme.text.inverse}`} />
                      </div>
                      <span className="font-medium">{subitem.name}</span>
                    </div>
                    <ArrowRightIcon className={`w-4 h-4 ${theme.text.muted} group-hover:${theme.text.primary} transition-all duration-200`} />
                  </button>
                ) : (
                  // Regular subitem
                  <button
                    onClick={() => {
                      if (subitem.href) {
                        navigate(subitem.href);
                        setActiveDrawer(null);
                        setDrawerData({
                          level2: null,
                          level3: null,
                          level4: null
                        });
                      }
                    }}
                    className={`w-full flex items-center space-x-3 px-4 py-3 text-left ${theme.text.secondary} hover:${theme.colors.background} hover:${theme.text.primary} rounded-xl transition-all duration-200 ${theme.shadow.sm} hover:${theme.shadow.default} hover:scale-[1.02] group ${
                      subitem.href && isCurrentPath(subitem.href) 
                        ? (theme.sidebar?.drawerSelected ||
                          `${theme.colors.primary} ${theme.text.inverse} ${theme.shadow.lg}`)
                        : ''
                    }`}
                  >
                    <div className={`p-1.5 rounded-lg ${theme.shadow.sm} group-hover:${theme.shadow.default} transition-all duration-200 ${
                      subitem.href && isCurrentPath(subitem.href)
                        ? theme.sidebar?.drawerAccent || `${theme.colors.accent} ${theme.text.inverse}`
                        : `${theme.colors.accent} ${theme.text.inverse}`
                    }`}>
                      <ArrowRightIcon className={`w-4 h-4 ${
                        subitem.href && isCurrentPath(subitem.href)
                          ? theme.sidebar?.activeIcon || theme.text.inverse
                          : theme.text.inverse
                      }`} />
                    </div>
                    <span className="font-medium">{subitem.name}</span>
                    {subitem.href && isCurrentPath(subitem.href) && (
                      <div className={`ml-auto h-2 w-2 ${theme.colors.success} rounded-full animate-pulse`}></div>
                    )}
                  </button>
                )}
              </div>
            ))}
          </div>
        </div>

        {/* Drawer Footer */}
        <div className={`flex-shrink-0 p-4 border-t ${theme.border.light} ${theme.gradients.subtle}`}>
          <div className={`${theme.gradients.background} rounded-lg p-3 border ${theme.border.light} ${theme.shadow.sm}`}>
            <div className="flex items-center space-x-2">
              <div className={`h-2 w-2 ${theme.colors.success} rounded-full animate-pulse`}></div>
              <span className={`text-xs ${theme.text.muted} font-medium`}>
                {item.subitems.length} {item.subitems.length === 1 ? 'item' : 'items'} available
              </span>
            </div>
          </div>
        </div>
      </div>
    );
  };

  const sidebarCollapseToggle = (extraClassName = '') =>
    !isCollapsible ? null : (
      <button
        type="button"
        onClick={toggleCollapsed}
        aria-expanded={!collapsed}
        aria-label={collapsed ? 'Expand sidebar' : 'Collapse sidebar'}
        className={cn(
          'group flex items-center justify-center rounded-lg shrink-0',
          'w-9 h-9 transition-all duration-200 ease-out',
          'focus:outline-none focus-visible:ring-2 focus-visible:ring-offset-1',
          collapsed && 'w-10 h-10',
          useBrandHeader
            ? logoCollapseBtnClass
            : theme.sidebar?.collapseBtn ??
                cn(
                  'bg-white/25 hover:bg-white/40',
                  'border border-white/40 hover:border-white/55',
                  'shadow-md hover:shadow-lg',
                  'ring-1 ring-inset ring-white/20',
                  'focus-visible:ring-white/60 focus-visible:ring-offset-blue-800',
                ),
          !useBrandHeader &&
            isDark &&
            'focus-visible:ring-slate-400 focus-visible:ring-offset-slate-900',
          useBrandHeader &&
            (isDark
              ? 'focus-visible:ring-slate-500 focus-visible:ring-offset-slate-900'
              : 'focus-visible:ring-blue-400 focus-visible:ring-offset-white'),
          extraClassName,
        )}
        title={collapsed ? 'Expand sidebar' : 'Collapse sidebar'}
      >
        <ChevronLeftIcon
          className={cn(
            'transition-transform duration-300 ease-out',
            collapsed ? 'w-5 h-5' : 'w-[1.125rem] h-[1.125rem]',
            useBrandHeader
              ? theme.text.secondary
              : theme.sidebar?.collapseBtnIcon ??
                  theme.sidebar?.logo ??
                  theme.text.inverse,
            'group-hover:scale-110',
            collapsed && 'rotate-180',
          )}
          strokeWidth={2.5}
        />
      </button>
    );

  return (
    <>
      {/* Mobile sidebar overlay */}
      {isOpen && (
        <div 
          className="fixed inset-0 bg-black bg-opacity-50 z-20 lg:hidden backdrop-blur-sm transition-opacity duration-300"
          onClick={() => setIsOpen(false)}
        />
      )}
      
      {/* Sidebar */}
      <div 
        data-testid="app-sidebar"
        className={`
          fixed inset-y-0 left-0 z-50 ${theme.gradients.sidebar} ${theme.shadow.xl} transform transition-all duration-300 ease-out lg:translate-x-0 lg:static lg:inset-0 border-r ${theme.border.light} flex flex-col
          ${isOpen ? 'translate-x-0' : '-translate-x-full'}
        `}
        style={{ 
          width: collapsed ? collapsedWidth : expandedWidth 
        }}
      >
        {/* Header — logo centered between left edge and collapse button */}
        <div
          className={cn(
            'flex flex-shrink-0 w-full',
            collapsed
              ? 'flex-col items-center justify-center gap-1.5 h-[4.5rem] px-2'
              : companyLogoUrl
                ? 'grid grid-cols-[1.75rem_minmax(0,1fr)_2.25rem] items-center h-[4.5rem] px-2 gap-1'
                : 'relative items-center justify-between h-[4.5rem] px-4',
            useBrandHeader
              ? topHeaderMatchClass
              : theme.sidebar?.header ??
                  cn(
                    theme.colors.primary,
                    theme.text.inverse,
                    'border-b',
                    theme.border.light,
                  ),
          )}
        >
          {collapsed ? (
            companyLogoUrl ? (
              <img
                src={companyLogoUrl}
                alt=""
                className="h-7 w-auto max-w-[58px] object-contain object-center"
                onError={() => setCompanyLogoFailed(true)}
              />
            ) : (
              <ShieldCheckIcon
                className={cn(
                  'w-5 h-5 shrink-0',
                  theme.sidebar?.logo ?? theme.text.inverse,
                )}
                aria-hidden
              />
            )
          ) : companyLogoUrl ? (
            <>
              <div className="shrink-0" aria-hidden="true" />
              <div className="flex items-center justify-center min-w-0 px-1">
                <img
                  src={companyLogoUrl}
                  alt=""
                  className="h-10 w-auto max-w-full object-contain object-center -translate-x-0.5"
                  onError={() => setCompanyLogoFailed(true)}
                />
              </div>
              {sidebarCollapseToggle('justify-self-center')}
            </>
          ) : (
            <div className="flex items-center space-x-3 min-w-0 flex-1 mr-2">
              <div
                className={cn(
                  theme.sidebar?.headerIconBox ??
                    cn(
                      'p-2 rounded-lg backdrop-blur-sm',
                      theme.text.inverse,
                      'bg-white/20',
                    ),
                )}
              >
                <ShieldCheckIcon
                  className={cn(
                    'w-6 h-6 shrink-0',
                    theme.sidebar?.logo ?? theme.text.inverse,
                  )}
                  aria-hidden
                />
              </div>
              <div className="min-w-0">
                <h2
                  className={cn(
                    'text-xl font-bold truncate',
                    theme.sidebar?.logo ?? theme.text.inverse,
                  )}
                >
                  QMS
                </h2>
                <p
                  className={cn(
                    'text-xs truncate',
                    theme.sidebar?.logoMuted ??
                      cn(theme.sidebar?.logo ?? theme.text.inverse, 'opacity-80'),
                  )}
                >
                  Quality Management
                </p>
              </div>
            </div>
          )}

          {collapsed && sidebarCollapseToggle()}
          {!collapsed &&
            !companyLogoUrl &&
            sidebarCollapseToggle('absolute right-2 top-1/2 -translate-y-1/2')}
        </div>
        
        {/* Scrollable Navigation Area */}
        <nav 
          data-testid="app-sidebar-nav"
          className={`flex-1 overflow-y-auto overflow-x-hidden ${collapsed ? 'px-2 py-6' : 'px-4 py-6'} ${theme.gradients.background} relative`} 
          style={{ 
            minHeight: 0,
            maxHeight: 'calc(100vh - 200px)', // Account for header and footer
            scrollbarWidth: 'thin',
            scrollbarColor: `${theme.border?.light || '#e5e7eb'} transparent`
          }}
        >
          {showSidebarSearch && (
            <SidebarMenuSearch
              value={menuSearch}
              onChange={setMenuSearch}
              collapsed={collapsed}
            />
          )}

          {!collapsed && showSidebarFavorites && favoriteLinks.length > 0 && (
            <div className="px-2 pb-3 mb-1 border-b border-black/5 dark:border-white/10">
              <p
                className={cn(
                  'text-[10px] font-semibold uppercase tracking-wider mb-2 px-1',
                  theme.text.muted
                )}
              >
                Favorites
              </p>
              <div className="space-y-1">
                {favoriteLinks.map((fav) => {
                  const active = isCurrentPath(fav.href);
                  return (
                    <button
                      key={fav.href}
                      type="button"
                      onClick={() => {
                        navigate(fav.href);
                        if (isOpen) setIsOpen(false);
                      }}
                      className={cn(
                        'w-full flex items-center gap-2 px-2 py-1.5 text-sm rounded-lg text-left transition-colors',
                        active
                          ? theme.sidebar?.selected
                            ? cn(theme.sidebar.selected)
                            : cn(theme.colors.primary, theme.text.inverse, theme.shadow.sm)
                          : cn(theme.text.secondary, `hover:${theme.colors.background}`)
                      )}
                    >
                      {fav.icon ? (
                        <svg
                          className="h-4 w-4 shrink-0"
                          fill="none"
                          viewBox="0 0 24 24"
                          stroke="currentColor"
                        >
                          <path
                            strokeLinecap="round"
                            strokeLinejoin="round"
                            strokeWidth={2}
                            d={fav.icon}
                          />
                        </svg>
                      ) : (
                        <StarIcon className="h-4 w-4 shrink-0 fill-amber-400 stroke-amber-500" />
                      )}
                      <span className="truncate flex-1">{fav.name}</span>
                    </button>
                  );
                })}
              </div>
            </div>
          )}

          <div className="space-y-2">
            {displayMenuItems.length === 0 ? (
              <div className="text-center py-12">
                <div className={`mx-auto h-12 w-12 ${theme.text.muted} mb-4`}>
                  <DocumentTextIcon className="h-12 w-12" />
                </div>
                <p className={`text-sm ${theme.text.secondary} font-medium`}>
                  {menuSearch.trim() ? 'No matching menu items' : 'No menu items available'}
                </p>
                {!menuSearch.trim() && (
                  <p className={`text-xs ${theme.text.muted} mt-1`}>Role: {userRole}</p>
                )}
              </div>
            ) : (
              displayMenuItems.map((item) => renderMenuItem(item))
            )}
          </div>
          
        </nav>
        
        {/* Enhanced Footer - User Section with Logout */}
        <div className={`flex-shrink-0 ${collapsed ? 'p-2' : 'p-4'} border-t ${theme.border.light} ${theme.gradients.sidebar}`}>
          <div className={`${theme.gradients.subtle} rounded-xl ${collapsed ? 'p-2' : 'p-4'} border ${theme.border.light} ${theme.shadow.sm}`}>
            {/* User Info */}
            <div className={`flex items-center ${collapsed ? 'justify-center' : 'mb-3'}`}>
              <div className="flex-shrink-0">
                <div className={`h-10 w-10 ${theme.colors.primary} ${theme.text.inverse} rounded-xl flex items-center justify-center font-bold text-sm ${theme.shadow.lg}`}>
                  {user?.userFirstName?.[0] || 'U'}{user?.userLastName?.[0] || 'U'}
                </div>
              </div>
              {!collapsed && (
                <div className="ml-3 min-w-0 flex-1">
                  <p className={`text-sm font-semibold ${theme.text.primary} truncate`}>
                    {user?.userDisplayName || `${user?.userFirstName || ''} ${user?.userLastName || ''}`.trim() || 'User'}
                  </p>
                  <div className="flex items-center gap-2">
                    <p className={`text-xs ${theme.text.muted} truncate capitalize`}>
                      {user?.role?.name || userRole || 'Administrator'}
                    </p>
                    <div className={`h-2 w-2 ${theme.colors.success.replace('bg-', 'bg-')} rounded-full animate-pulse`}></div>
                  </div>
                </div>
              )}
            </div>
            
            {/* User Actions */}
            {!collapsed && (
              <div className="flex items-center gap-2">
                <button
                  type="button"
                  onClick={() => setIsProfileModalOpen(true)}
                  className={`flex-1 flex items-center justify-center gap-2 px-3 py-2 text-xs font-medium ${theme.colors.surface} ${theme.text.secondary} hover:${theme.colors.background} hover:${theme.text.primary} rounded-lg transition-all duration-200 ${theme.border.light} border`}
                >
                  <UserIcon className="w-3 h-3" />
                  Profile
                </button>
                <button
                  type="button"
                  onClick={onLogout}
                  className={`flex-1 flex items-center justify-center gap-2 px-3 py-2 text-xs font-medium ${theme.colors.secondary} ${theme.sidebar?.logo || theme.text.inverse} hover:opacity-90 rounded-lg transition-all duration-200 ${theme.shadow.sm} hover:${theme.shadow.default}`}
                >
                  <ArrowRightOnRectangleIcon className="w-3 h-3" />
                  Logout
                </button>
              </div>
            )}
            
            {/* Collapsed state - Show only logout button */}
            {collapsed && (
              <div className="flex justify-center mt-2">
                <button 
                  onClick={onLogout}
                  className={`flex items-center justify-center w-8 h-8 rounded-lg ${theme.colors.secondary} ${theme.sidebar?.logo || theme.text.inverse} hover:opacity-90 transition-all duration-200 ${theme.shadow.sm} hover:${theme.shadow.default}`}
                  title="Logout"
                >
                  <ArrowRightOnRectangleIcon className="w-4 h-4" />
                </button>
              </div>
            )}
            
            {/* App Version */}
            {appVersion && (
              <div className={`mt-3 pt-3 border-t ${theme.border.light}`}>
                <p className={`text-xs ${theme.text.muted} text-center`}>
                  Version {appVersion}
                </p>
              </div>
            )}
          </div>
        </div>
      </div>

      {/* Backdrop for Drawers */}
      {activeDrawer && (
        <div 
          className="fixed inset-0 bg-black bg-opacity-30 z-30 backdrop-blur-sm transition-opacity duration-300"
          style={{ left: '80px' }} // Don't cover the sidebar
          onClick={() => {
            setActiveDrawer(null);
            setDrawerData({
              level2: null,
              level3: null,
              level4: null
            });
          }}
        />
      )}

      {/* Cascading Drawers for Collapsed Mode - Positioned to keep sidebar visible */}
      {renderDrawer('level2', drawerData.level2, 80)}
      {renderDrawer('level3', drawerData.level3, 80)}
      {renderDrawer('level4', drawerData.level4, 80)}

      {/* Profile Modal */}
      <ProfileModal
        isOpen={isProfileModalOpen}
        onClose={() => setIsProfileModalOpen(false)}
      />
    </>
  );
};

export default Sidebar;